/**
 * 任务跟踪表 / 节点汇总 / 延期统计（0903）的返回结构。
 * 每行能做什么（can.*）是后端算好的，前端只按它显示按钮，不再复制一套权限判断。
 */
export type TrackerScope = 'all' | 'mine' | 'sent' | 'team';

export interface TrackerPerson {
  id: number | null;
  name: string | null;
}

/** 共同一条里的一位责任人：各自一条投影、各自一个状态 */
export interface TrackerOwner {
  id: number;
  name: string | null;
  workItemId: number;
  status: string;
  updatedAt: string | null;
}

export interface TrackerItem {
  id: number;
  workType: string;
  /** delegation = 派活；self = 派给自己（自建）；meeting = 会议待办 */
  source: 'delegation' | 'self' | 'meeting' | 'plan' | 'issue';
  issueId?: number;
  issueCode?: string | null;
  severityGrade?: string | null;
  requiredDueAt?: string | null;
  completedAt?: string | null;
  latestProgress?: string | null;
  /** 项目入口自动带入的阶段逾期事项；操作回到原节点。 */
  nodeId?: number;
  phaseName?: string | null;
  progress?: number | null;
  /** 完成文件门槛，非业务完成或审批结论。 */
  completionEvidence?: { deliverables: number; missingTitles: string[] };
  /** 阶段事项按东八区自然日距当前期限的天数，负数为逾期。 */
  dueInDays?: number;
  title: string;
  /** 多责任人时是合成状态：都完成才完成 */
  status: string;
  priority: string;
  dueAt: string | null;
  createdAt: string;
  updatedAt: string;
  overdue: boolean;
  /** 第一责任人（老字段，保留） */
  owner: TrackerPerson | null;
  owners: TrackerOwner[];
  ownerNames: string;
  /** 我自己那条投影（我是责任人之一时）；状态动作都作用在它上面 */
  myWorkItemId: number | null;
  myStatus: string | null;
  assigner: TrackerPerson | null;
  selfAssigned: boolean;
  taskType: string | null;
  note: string | null;
  project: { id: number; projectNo: string | null; projectName: string | null } | null;
  reference: { type: string; id: string | null } | null;
  delegationId: number | null;
  hold: {
    reason?: string; fromStatus?: string; requestedBy?: string | null;
    approvedBy?: string; approvedAt?: string; note?: string | null;
  } | null;
  holdRequest: {
    id: number | null; reason: string | null; requestedBy: string | null; requestedAt: string | null; approvers: string[];
  } | null;
  reminders: number;
  lastRemindedAt: string | null;
  /** 往后改过几次期限（提前的不算） */
  reschedules: number;
  /**
   * 一共往后推了多少天（0916）。
   *
   * 和 `reschedules` 是两件事：线上 815 那条只改过 1 次，却一口气推了 15 天；
   * 360 那条改了 3 次共 21 天。只看次数会把前者读成"还好"。
   */
  delayDays: number;
  /** 最初那个期限；有它才看得出漂了多远（原定 09/09 → 现在 09/30） */
  originalDueAt: string | null;
  lastRescheduledAt: string | null;
  lastRescheduleReason: string | null;
  /** 编辑留痕 + 补充记录的条数，和最近一条 */
  noteCount: number;
  lastNote: { id: number; kind: string; body: string; actorName: string | null; createdAt: string } | null;
  meeting: { id: number; title: string | null } | null;
  can: {
    updateStatus: boolean;
    requestHold: boolean;
    decideHold: boolean;
    remind: boolean;
    cancel: boolean;
    reschedule: boolean;
    /** 写错了能改：派发人 / 责任人 / 管理员；会议待办不在这里改 */
    edit: boolean;
    /** 补充记录：看得见就能写一句 */
    note: boolean;
  };
}

export interface TaskTimelineEntry {
  at: string;
  kind: 'created' | 'edit' | 'note' | 'reschedule' | 'hold_request' | 'hold_decided' | 'status' | 'reminded' | 'cancelled';
  actor: string | null;
  text: string;
  meta?: Record<string, unknown>;
}

export interface TaskTimelineResponse {
  items: TaskTimelineEntry[];
  owners: Array<{ id: number; name: string | null; status: string }>;
}

export interface TrackerResponse {
  identity: { id: number; displayName: string; isAdmin: boolean; canViewDelayStats: boolean };
  scope: TrackerScope;
  total: number;
  returned: number;
  truncated: boolean;
  team: { subordinates: number; managedProjects: number };
  items: TrackerItem[];
}

export interface TaskTypeSuggestion {
  value: string;
  total: number;
}

export const DELAY_CATEGORIES = [
  '客户未反馈', '供应商未交付', '内部资源冲突', '需求变更', '上游节点延后', '本人排期调整', '其他',
] as const;
export type DelayCategory = (typeof DELAY_CATEGORIES)[number];

export interface DelayPersonRow {
  userId: number;
  name: string | null;
  leaderName: string | null;
  open: number;
  overdue: number;
  maxOverdueDays: number;
  owed: number;
  selfDelays: number;
  delayDaysTotal: number;
  repeatItems: number;
  parked: number;
  reminders: number;
  completedLate: number;
}

export interface DelayStatsResponse {
  categories: readonly string[];
  days: number;
  scope: 'all' | 'team';
  generatedAt: string;
  rules: { owed: string; selfDelay: string };
  totals: {
    open: number; overdue: number; owed: number; selfDelays: number; managerMoves: number; repeatItems: number;
    parked: number; holdsPending: number; holdsApproved: number; holdsRejected: number; reminders: number; completedLate: number;
  };
  byPerson: DelayPersonRow[];
  byReason: Array<{ category: string; total: number; days: number }>;
  repeatItems: Array<{
    id: number; title: string; status: string; ownerName: string | null; assignerName: string | null; projectNo: string | null;
    dueAt: string | null; firstDueAt: string | null; times: number; delayDaysTotal: number;
    lastReason: string | null; lastCategory: string | null;
  }>;
  owed: Array<{
    id: number; title: string; ownerName: string | null; assignerName: string | null; projectNo: string | null;
    dueAt: string | null; overdueDays: number; reminders: number;
  }>;
  recent: Array<{
    id: number; itemId: number; title: string; ownerName: string | null; projectNo: string | null;
    actorName: string | null; actorRole: string; byOwner: boolean;
    fromDueAt: string | null; toDueAt: string; delayDays: number; category: string | null; reason: string; createdAt: string;
  }>;
}

export type NodeSummaryScope = 'open' | 'overdue' | 'week' | 'nodate' | 'done' | 'all';

export interface NodeSummaryItem {
  id: number;
  projectId: number;
  projectNo: string;
  projectName: string;
  name: string;
  phaseName: string | null;
  ownerUserId: number | null;
  ownerName: string | null;
  /** 责任人是不是绑到了账号；没绑的催办和「我的」都找不到它 */
  ownerLinked: boolean;
  /** 协办人姓名，顿号分隔；没有就是 null */
  helperNames?: string | null;
  plannedStartAt: string | null;
  plannedEndAt: string | null;
  actualAt: string | null;
  status: string;
  isKeyDeliverable: boolean;
  done: boolean;
  open: boolean;
  overdue: boolean;
  overdueDays: number | null;
  /** 改期留痕（0926）：往后推过几次、累计几天、最初定的哪天、最近一次「分类：原因」 */
  reschedules?: number;
  delayDaysTotal?: number;
  originalPlannedEndAt?: string | null;
  lastRescheduleReason?: string | null;
}

/** 阶段计划的延期统计（0926，管理视角）：和派活那份并排看 */
export interface NodeDelayActorRow {
  userId: number | null;
  name: string | null;
  leaderName: string | null;
  moves: number;
  pushed: number;
  pulled: number;
  cleared: number;
  delayDaysTotal: number;
  avgPushDays: number;
  pushedWhenOverdue: number;
  projects: number;
  repeatItems: number;
}

export interface NodeDelayOwnerRow {
  userId: number;
  name: string | null;
  leaderName: string | null;
  selfDelays: number;
  managerMoves: number;
  delayDaysTotal: number;
  repeatItems: number;
  nodes: number;
}

export interface NodeDelayStatsResponse {
  categories: readonly string[];
  days: number;
  scope: 'all' | 'team';
  generatedAt: string;
  rules: { required: string; role: string };
  totals: {
    moves: number; pushed: number; pulled: number; cleared: number;
    selfDelays: number; managerMoves: number; delayDaysTotal: number; avgPushDays: number;
    pushedWhenOverdue: number; nodesTouched: number; repeatItems: number;
  };
  byActor: NodeDelayActorRow[];
  byOwner: NodeDelayOwnerRow[];
  byReason: Array<{ category: string; total: number; days: number }>;
  bySource: Array<{ source: string; total: number }>;
  repeatItems: Array<{
    nodeId: number; projectId: number; name: string | null; projectNo: string | null; ownerName: string | null;
    times: number; delayDaysTotal: number; firstFrom: string | null; currentPlannedEndAt: string | null;
    lastReason: string | null; lastCategory: string | null; lastActor: string | null;
  }>;
  recent: Array<{
    id: number; nodeId: number; projectId: number; name: string | null; projectNo: string | null; ownerName: string | null;
    actorName: string | null; actorRole: string; byOwner: boolean; source: string;
    fromPlannedEndAt: string | null; toPlannedEndAt: string | null; delayDays: number | null;
    category: string | null; reason: string; createdAt: string;
  }>;
}

/** 经营快照趋势（0926）：总监看板的「较上周」 */
export type SnapshotMetrics = Record<string, number>;
export interface SnapshotSeries {
  key: string;
  label: string | null;
  latest: SnapshotMetrics | null;
  baseline: SnapshotMetrics | null;
  series: Array<{ date: string; metrics: SnapshotMetrics }>;
}
export interface SnapshotTrends {
  weeks: number;
  metricsVersion: number;
  dates: string[];
  latestDate: string | null;
  baselineDate: string | null;
  company: SnapshotSeries | null;
  managers: SnapshotSeries[];
  projects: SnapshotSeries[];
}

/** 组头用的真数字：统计的是整个筛选结果，不是当前加载的那一页 */
export interface NodeSummaryProjectRow {
  projectId: number;
  projectNo: string;
  projectName: string;
  open: number;
  overdue: number;
  nodate: number;
  unowned: number;
  done: number;
  total: number;
}

export interface NodeSummaryOwnerRow {
  ownerUserId: number | null;
  ownerName: string | null;
  /** 点开这一行时原样回传给后端筛；别在前端拼，拼错了就是「标 72 点进去 0 条」 */
  ownerKey: string;
  /** 责任人栏里填的是岗位名（「项目经理」这种）而不是人，别混进人头统计 */
  isRole: boolean;
  open: number;
  overdue: number;
  nodate: number;
  projects: number;
}

export interface NodeSummaryResponse {
  scope: NodeSummaryScope;
  counts: {
    all: number; open: number; overdue: number; week: number;
    nodate: number; done: number; unowned: number;
  };
  total: number;
  offset: number;
  returned: number;
  truncated: boolean;
  items: NodeSummaryItem[];
  byProject: NodeSummaryProjectRow[];
  byOwner: NodeSummaryOwnerRow[];
}
